import { zodResolver } from "@hookform/resolvers/zod";
import * as Accordion from "@radix-ui/react-accordion";
import type { ZonedDateTime } from "@sa-nu/shared-utils/date";
import {
  formatZonedDateTime,
  getCurrentDate,
  getCurrentTime,
  PlainDate,
} from "@sa-nu/shared-utils/date";
import clsx from "clsx";
import Image from "next/image";
import { useRouter, useSearchParams } from "next/navigation";
import type { ComponentProps } from "react";
import React, { useEffect, useMemo, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { z } from "zod";

import { graphql, useFragment } from "../../../../__generated__/gql";
import type { FragmentType } from "../../../../__generated__/gql";
import type { AccommodationReservationProvider } from "../../../../__generated__/gql/graphql";
import { StayTicketValidationError } from "../../../../__generated__/gql/graphql";
import {
  GUEST_STAY_GUIDE_URL,
  RESERVATION_CLOSE_THRESHOLD_HOUR,
} from "../../../../constants";
import { AdditionalNoticeArea } from "../../../_components/reservation-form/additional-notice-area/additional-notice-area";
import { ReservationFormBase } from "../../../_components/reservation-form/reservation-form-base";
import { RoomDetailDrawerProvider } from "../../../_components/room-detail/room-detail-drawer-provider";
import { buildLeadTimeUnavailablePeriods } from "../../../_components/room-type-reservation/_utils/unavailable-periods";
import { RoomTypeReservationContainer } from "../../../_components/room-type-reservation/room-type-reservation-container";
import { FormItem } from "../../../_components/ui/form-item";
import { StepByStepGuide } from "../../../_components/ui/step-by-step-guide/step-by-step-guide";
import type { TicketReservationConditionType } from "../_components/reservation-conditions";
import { TicketReservationConditions } from "../_components/reservation-conditions";
import { TicketNotice } from "../_components/ticket-notice";

import { shouldUseNewRoomTypeExperience } from "./should-use-new-room-type-experience";
import { TicketRoomDetailFlowBridge } from "./ticket-room-detail-flow-bridge";

const FragmentOfStayTicketValidationResult = graphql(`
  fragment StayTicketValidationResult on StayTicketValidationResult {
    stayTicketCode
    validationError
  }
`);

const FragmentOfStayTicketUnit = graphql(`
  fragment StayTicketUnitForTicketReservationForm on StayTicketUnit {
    id
    stayTicketMasterId
    reservationInfo {
      type
      name
      notice
      availableSites {
        id
        name
        area {
          id
        }
      }
      availableArchitectureTypes {
        id
        name
      }
      #baseAccommodationFeePolicy {
      #  dailyFeePolicy
      #}
      additionalNoticeTypes
      additionalGuestFeeStartsAt
    }
    stayTickets {
      id
      code
      expiresAt
      stayStartNight
      stayEndNight
    }
    stayStartNight
    stayEndNight
    ...TicketReservationConditions
  }
`);

// NOTE: 横幅に収めるために文言を短くした。<FormItem> を改善して複数行で表示できるようにしたい。
// 正式な文言は下記参照
// https://www.notion.so/sanu/b2e57f3070134a99b06f0cfd114f1163?pvs=4#7350974821e64fb6b8aea2214986b341
const errorMessages: Record<string, string> = {
  [StayTicketValidationError.Expired]: "有効期限が切れています。",
  [StayTicketValidationError.NotFound]:
    "チケットが存在しないため、ご利用できません。",
  [StayTicketValidationError.MasterMismatch]:
    "適用済みのチケットと異なる種類のチケットです。",
  [StayTicketValidationError.UnitFull]: "上限連泊数の4泊を超えています。",
  [StayTicketValidationError.Unusable]:
    "チケットがすでに使用済みまたは無効です。",
  [StayTicketValidationError.Duplicate]: "同じチケットが複数指定されています。",
};

// Accordion の仕様に合わせて定義
const AccordionStatus = {
  open: "code",
  close: "",
};

// チケット予約の初期表示日付オフセット（日数）
const TICKET_RESERVATION_INITIAL_DATE_OFFSET = 28;

export const _getMinNight = getMinNight;
function getMinNight(
  minNight: PlainDate,
  currentTime: ZonedDateTime = getCurrentTime(),
): PlainDate {
  const today = currentTime.toPlainDate();
  const currentHour = currentTime.hour;
  const minNightFromToday = today.add({
    days: currentHour >= RESERVATION_CLOSE_THRESHOLD_HOUR ? 1 : 0,
  });
  return PlainDate.compare(minNightFromToday, minNight) < 0
    ? minNight
    : minNightFromToday;
}

function getTicketReservationInitialDate(
  minNight: PlainDate,
  maxNight: PlainDate | null | undefined,
  initialDateOffset: number,
  currentTime: ZonedDateTime = getCurrentTime(),
): PlainDate {
  const today = currentTime.toPlainDate();
  const targetDate = today.add({ days: initialDateOffset });

  if (maxNight && PlainDate.compare(targetDate, maxNight) > 0) {
    return minNight;
  }

  // getMinNight()の出力と今日+initialDateOffsetのいずれか遅い方を返す
  return PlainDate.compare(targetDate, minNight) < 0 ? minNight : targetDate;
}

const MAX_NUM_OF_CODE = 4;

const defaultValues = {
  codes: Array.from({ length: MAX_NUM_OF_CODE }).map(() => {
    return { stayTicketCode: "", validationError: null };
  }),
};

const FormSchema = z
  .object({
    codes: z.array(
      z.object({
        stayTicketCode: z.string().trim(),
        validationError: z.string().trim().nullable(),
      }),
    ),
  })
  .refine((data) => {
    return data.codes.some((code) => code.stayTicketCode.length > 0);
  });
type FormValues = z.infer<typeof FormSchema>;

type StayTicketValidationResultData = FragmentType<
  typeof FragmentOfStayTicketValidationResult
>;
type StayTicketUnitData = FragmentType<typeof FragmentOfStayTicketUnit>;

export function TicketReservationForm({
  validationResultData,
  stayTicketUnitData,
  originalReservationData,
  loading = false,
  shouldOpenCodeArea = false,
  ticketFormAreaHidden = false,
  fromDateOverride,
  useNewRoomDetailFlow = false,
}: {
  validationResultData?: StayTicketValidationResultData[];
  stayTicketUnitData: StayTicketUnitData | null;
  originalReservationData: ComponentProps<
    typeof ReservationFormBase
  >["originalReservationData"];
  loading?: boolean;
  shouldOpenCodeArea?: boolean;
  ticketFormAreaHidden?: boolean;
  fromDateOverride?: PlainDate;
  useNewRoomDetailFlow?: boolean;
}) {
  const isTicketCodeEditable = originalReservationData === null;
  const router = useRouter();
  const searchParams = useSearchParams();

  const stayTicketUnit = useFragment(
    FragmentOfStayTicketUnit,
    stayTicketUnitData,
  );
  const validationResult = useFragment(
    FragmentOfStayTicketValidationResult,
    validationResultData,
  );

  const form = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues,
  });
  const {
    register,
    reset,
    setValue,
    control,
    handleSubmit,
    formState: { isValid, isDirty },
  } = form;

  const { fields } = useFieldArray({
    control,
    rules: { maxLength: MAX_NUM_OF_CODE, minLength: MAX_NUM_OF_CODE },
    name: "codes",
  });

  const [openCodeArea, setOpenCodeArea] = useState(AccordionStatus.close);
  const [codesSubmitted, setCodesSubmitted] = useState(false);

  useEffect(() => {
    if (validationResult) {
      const codes = Array.from({ length: MAX_NUM_OF_CODE }).map((_, index) => {
        return (
          validationResult[index] || {
            stayTicketCode: "",
            validationError: null,
          }
        );
      });
      reset({ codes });
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Reset form state on validation result change
      setCodesSubmitted(false);
      return;
    }
    if (stayTicketUnit) {
      const length = isTicketCodeEditable
        ? MAX_NUM_OF_CODE
        : stayTicketUnit.stayTickets.length;
      const codes = Array.from({ length }).map((_, index) => {
        const ticket = stayTicketUnit.stayTickets[index];
        return ticket
          ? { stayTicketCode: ticket.code, validationError: null }
          : { stayTicketCode: "", validationError: null };
      });
      reset({ codes });
      setCodesSubmitted(false);
    }
  }, [isTicketCodeEditable, reset, stayTicketUnit, validationResult]);

  // 適用するボタンをしたとき（フォームのコードを適用）
  function handleApplyTickets(formData: FormValues) {
    const codes = formData.codes
      .map((code) => code.stayTicketCode)
      .filter((code) => code.length > 0);
    if (codes.length > 0) {
      const codeString = encodeURIComponent(codes.join("_"));
      const newParams = new URLSearchParams(searchParams.toString());
      newParams.set("code", codeString);
      const url = `/ticket-reservations/form?${newParams.toString()}`;
      router.replace(url, { scroll: false });
      setCodesSubmitted(true);
    }
  }

  // コードエリア（アコーディオン）を開くかどうかの制御
  useEffect(() => {
    // 開く指示があれば開く
    if (shouldOpenCodeArea) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Sync accordion state with prop
      setOpenCodeArea(AccordionStatus.open);
      return;
    }
    // ローディング中は開かない
    if (!validationResult) {
      return;
    }
    // 一つでもエラーがあれば開く
    if (validationResult.some((r) => r.validationError)) {
      setOpenCodeArea(AccordionStatus.open);
      return;
    }
    // コードが１つも入力されていなければ開く
    if (validationResult.every((r) => !r.stayTicketCode)) {
      setOpenCodeArea(AccordionStatus.open);
      return;
    }
  }, [loading, shouldOpenCodeArea, stayTicketUnit, validationResult]);

  // チケットの適用状況を表示
  const ticketSummary = useMemo(() => {
    if (loading) {
      return "チケット確認中...";
    }
    if (validationResult) {
      const numOfValidCodes = validationResult.filter(
        (result) => !result.validationError,
      ).length;
      if (validationResult.length - numOfValidCodes > 0) {
        return "チケットを確認してください";
      } else {
        return `${numOfValidCodes}枚のチケットが適用されました`;
      }
    }
    if (stayTicketUnit?.stayTickets && stayTicketUnit.stayTickets.length > 0) {
      return `${stayTicketUnit.stayTickets.length}枚のチケットが適用されました`;
    }
    return "";
  }, [loading, stayTicketUnit, validationResult]);

  function getExpirationDate(stayTicketCode: string) {
    const stayTicket = stayTicketUnit?.stayTickets.find(
      (ticket) => ticket.code === stayTicketCode,
    );
    return stayTicket?.expiresAt ?? null;
  }

  const provider: AccommodationReservationProvider | null = useMemo(() => {
    if (stayTicketUnit?.id) {
      return {
        __typename: "StayTicketAccommodationReservationProvider",
        stayTicketUnitId: stayTicketUnit.id,
      };
    } else {
      return null;
    }
  }, [stayTicketUnit]);
  const notice = stayTicketUnit?.reservationInfo.notice ?? null;
  const minNight =
    stayTicketUnit?.stayStartNight &&
    getMinNight(PlainDate.from(stayTicketUnit.stayStartNight));
  const maxNight =
    stayTicketUnit?.stayEndNight && PlainDate.from(stayTicketUnit.stayEndNight);
  const calculatedInitialDate =
    stayTicketUnit?.stayStartNight && minNight
      ? getTicketReservationInitialDate(
          minNight,
          maxNight,
          TICKET_RESERVATION_INITIAL_DATE_OFFSET,
        )
      : undefined;

  const initialDisplayDate = fromDateOverride || calculatedInitialDate;
  const additionalNoticeTypes =
    stayTicketUnit?.reservationInfo.additionalNoticeTypes ?? [];
  const addtionalNoticeArea = (
    <AdditionalNoticeArea additionalNoticeTypes={additionalNoticeTypes} />
  );

  return (
    <>
      {!ticketFormAreaHidden && (
        <>
          <section className="bg-slightbeige">
            <div className="mx-auto w-[315px] pt-[60px] pb-[30px] md:w-[690px]">
              <h2 className="text-[14px] font-bold">宿泊予約の方法</h2>
            </div>
          </section>
          <section className="bg-slightbeige">
            <div className="mx-auto w-[315px] md:w-[690px]">
              <StepByStepGuide
                instructions={[
                  "宿泊施設と日程を選ぶ",
                  "宿泊者情報を入力する",
                  "支払い情報を入力し、予約完了する",
                ]}
                stepLabel={true}
              />
              <p className="py-8 text-center text-[14px] font-medium tracking-[0.84px] md:text-[13px]">
                予約や滞在に関しては、
                <a
                  href={GUEST_STAY_GUIDE_URL}
                  target="_blank"
                  data-analytics-id="ticket_reservation_form_clicked_guest_stay_guide"
                  rel="noopener noreferrer"
                  className="font-bold underline"
                >
                  ゲストご宿泊ガイド
                </a>{" "}
                および予約完了後に届く「滞在のしおり」をご確認ください。
              </p>
            </div>
          </section>
          <section className="bg-slightbeige-104 py-[30px]">
            <div className="bg-slightbeige-112 mx-auto w-[315px] py-[30px] md:w-[690px]">
              <Accordion.Root
                type="single"
                collapsible={true}
                value={openCodeArea}
                onValueChange={setOpenCodeArea}
              >
                <Accordion.Item value={AccordionStatus.open}>
                  <Accordion.Header>
                    <Accordion.Trigger className="flex w-full items-center justify-stretch px-[20px]">
                      <div className="grow">
                        <h2 className="flex flex-col text-[12px] md:flex-row">
                          <span className="text-left font-bold">
                            チケットコード
                          </span>
                          <span className="text-left md:ml-4">
                            {ticketSummary}
                          </span>
                        </h2>
                      </div>
                      <div>
                        <Image
                          src="/i/icon_chevron_down.svg"
                          alt="チケットコード入力欄を開く"
                          width={24}
                          height={24}
                          className={clsx(
                            openCodeArea ? "rotate-180" : "rotate-0",
                            "origin-center transform transition-transform",
                          )}
                        />
                      </div>
                    </Accordion.Trigger>
                  </Accordion.Header>
                  <Accordion.Content>
                    {/* NOTE: コンポーネントとして切り出したい */}
                    <form onSubmit={handleSubmit(handleApplyTickets)}>
                      <div className="mx-auto mt-[30px] flex w-full flex-col gap-y-8 px-[20px] md:w-[305px] md:px-0">
                        {fields.map((field, index) => {
                          const expirationDate =
                            (field.stayTicketCode &&
                              getExpirationDate(field.stayTicketCode)) ??
                            undefined;
                          return (
                            <FormItem
                              key={field.id}
                              label={"チケットコード"}
                              error={
                                (field.validationError ?? undefined) &&
                                errorMessages[field.validationError!]
                              }
                              description={
                                expirationDate &&
                                `有効期限：${formatZonedDateTime(
                                  expirationDate,
                                )}`
                              }
                            >
                              <div className="flex justify-stretch gap-x-2">
                                <input
                                  type="text"
                                  {...register(`codes.${index}.stayTicketCode`)}
                                  disabled={loading || !isTicketCodeEditable}
                                  className="w-full"
                                />
                                {isTicketCodeEditable && (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setValue(
                                        `codes.${index}.stayTicketCode`,
                                        "",
                                        {
                                          shouldDirty: true,
                                          shouldValidate: true,
                                        },
                                      );
                                    }}
                                    disabled={loading}
                                    data-analytics-id="ticket_reservation_form_clicked_ticket_code_clear"
                                  >
                                    <Image
                                      src="/i/icon_remove.svg"
                                      alt="フォームのクリア"
                                      width={24}
                                      height={24}
                                    />
                                  </button>
                                )}
                              </div>
                            </FormItem>
                          );
                        })}
                        {isTicketCodeEditable && (
                          <button
                            type="submit"
                            className="btn-primary mx-auto w-full"
                            data-analytics-id="ticket_reservation_form_submitted_ticket_code"
                            disabled={
                              loading || !isValid || !isDirty || codesSubmitted
                            }
                          >
                            {loading || codesSubmitted
                              ? "適用中..."
                              : "適用する"}
                          </button>
                        )}
                      </div>
                    </form>
                  </Accordion.Content>
                </Accordion.Item>
              </Accordion.Root>
            </div>
            {notice && (
              <div className="mx-auto w-[315px] pt-[30px] md:w-[690px]">
                <TicketNotice notice={notice} />
              </div>
            )}
          </section>
        </>
      )}
      {!ticketFormAreaHidden && stayTicketUnit && (
        <section className="bg-[#F1E8D7] py-[30px]">
          <div className="mx-auto flex w-[315px] flex-col md:w-[690px] md:flex-row">
            <div className="md:basis-1/2">
              <h2 className="text-[12px] font-bold">
                キャビンと日付を選択する
              </h2>
            </div>
            <div className="mt-4 md:mt-0 md:basis-1/2">
              {/* NOTE: 型を揃える（as やめる） */}
              <TicketReservationConditions
                stayTicketUnitData={
                  stayTicketUnit as TicketReservationConditionType
                }
              />
            </div>
          </div>
        </section>
      )}
      <section className="bg-slightbeige">
        {provider &&
          minNight &&
          maxNight &&
          stayTicketUnit &&
          (shouldUseNewRoomTypeExperience({
            reservationType: stayTicketUnit.reservationInfo.type,
            originalReservationData,
          }) ? (
            // 新フロー: 部屋一覧UIを全幅で表示する（現行フローの幅制約・中央寄せ・下マージンは付けない）
            <RoomTypeReservationContainer
              stayTicketUnitId={stayTicketUnit.id}
              stayTicketMasterId={stayTicketUnit.stayTicketMasterId ?? null}
              minNight={minNight}
              maxStayNights={stayTicketUnit.stayTickets.length}
              unavailablePeriods={buildLeadTimeUnavailablePeriods(
                getCurrentDate(),
              )}
            />
          ) : (
            <div className="mx-auto mb-8 flex w-full flex-col items-center py-4 md:w-[764px]">
              <ReservationFormBaseWithOptionalDrawer
                useNewRoomDetailFlow={useNewRoomDetailFlow}
                provider={provider}
                originalReservationData={originalReservationData}
                minNight={minNight}
                maxNight={maxNight}
                initialDisplayDate={initialDisplayDate}
                addtionalNoticeArea={addtionalNoticeArea}
              />
            </div>
          ))}
      </section>
    </>
  );
}

function ReservationFormBaseWithOptionalDrawer({
  useNewRoomDetailFlow,
  provider,
  originalReservationData,
  minNight,
  maxNight,
  initialDisplayDate,
  addtionalNoticeArea,
}: {
  useNewRoomDetailFlow: boolean;
  provider: AccommodationReservationProvider;
  originalReservationData: ComponentProps<
    typeof ReservationFormBase
  >["originalReservationData"];
  minNight: PlainDate;
  maxNight: PlainDate;
  initialDisplayDate: PlainDate | undefined;
  addtionalNoticeArea: React.ReactNode;
}) {
  const core = (
    <ReservationFormBase
      provider={provider}
      originalReservationData={originalReservationData}
      // Ticket予約の場合はログイン不要なのでユーザー情報を連携しない
      userInfo={null}
      minNight={minNight}
      maxNight={maxNight}
      initialDate={initialDisplayDate}
      draftReservationProps={{
        additionalNotice: addtionalNoticeArea,
        termsAgreementNoticeHidden: true,
      }}
    />
  );

  if (!useNewRoomDetailFlow) return core;

  return (
    <RoomDetailDrawerProvider>
      <TicketRoomDetailFlowBridge>{core}</TicketRoomDetailFlowBridge>
    </RoomDetailDrawerProvider>
  );
}
